import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonNew, IButtonNewProps } from '@components-library/ButtonNew/ButtonNew';
import { ArrowIcon } from '@components-library/Icons/iconsComponents';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import type { MenuProps } from 'antd';
import { Dropdown as AntdDropdown, Space as AntdSpace } from 'antd';
import cn from 'classnames';
import styles from './Dropdown.styles.module.scss';

export interface IDropDownProps {
  options?: MenuProps['items'];
  size?: IButtonNewProps['size'];
  loading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  classNameRoot?: string;
  buttonClassName?: string;
  onSelect: (e: {
    domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
    key: string;
    keyPath: string[];
  }) => void;
  triggerComponent?: ((isActive: boolean) => React.ReactNode) | React.ReactNode;
  selectable?: boolean;
  selectedKeys?: string[];
}

export const Dropdown = ({
  size,
  options,
  children,
  loading,
  disabled,
  icon,
  triggerComponent,
  className,
  classNameRoot,
  selectable,
  selectedKeys,
  onSelect,
  buttonClassName,
}: IDropDownProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    setIsOpen(false);
    onSelect(e);
  };

  const customTriggerComponent = triggerComponent
    ? typeof triggerComponent === 'function'
      ? triggerComponent(isOpen)
      : triggerComponent
    : false;

  return (
    <AntdDropdown
      menu={{
        items: options,
        onClick: handleMenuClick,
        selectable,
        selectedKeys,
      }}
      trigger={['click']}
      onOpenChange={setIsOpen}
      disabled={disabled}
      className={className}
      rootClassName={cn(styles.root, { [styles.selectedCheckbox]: selectable }, classNameRoot)}>
      {customTriggerComponent || (
        <ButtonNew className={buttonClassName} size={size} isActive={isOpen} isLoading={loading}>
          <AntdSpace size={[4, 4]}>
            {icon}
            {typeof children === 'string' ? i18n.t(children as KeyMapKeys) : children}
            <ArrowIcon direction={isOpen ? 'top' : 'bottom'} />
          </AntdSpace>
        </ButtonNew>
      )}
    </AntdDropdown>
  );
};
